import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Json, Tables } from '@kit/supabase/database';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

// Push platform types
type PushPlatform = 'ios' | 'android' | 'web';

type PushTokenRow = Tables<'user_push_tokens'>;

/**
 * Map a typed `user_push_tokens` row into the narrowed domain PushToken.
 * Narrows `platform` (DB `string`) and `device_info` (DB `Json`) to the
 * structured shapes the UI consumes, instead of an unsafe blanket cast.
 */
function mapPushToken(row: PushTokenRow): PushToken {
  return {
    id: row.id,
    token: row.token,
    platform: row.platform as PushPlatform,
    device_info: (row.device_info ?? {}) as DeviceInfo,
    is_active: row.is_active,
    last_used_at: row.last_used_at,
    created_at: row.created_at,
  };
}

interface DeviceInfo {
  model?: string;
  osVersion?: string;
  appVersion?: string;
  deviceName?: string;
}

interface PushToken {
  id: string;
  token: string;
  platform: PushPlatform;
  device_info: DeviceInfo;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}

interface RegisterPushTokenParams {
  token: string;
  platform: PushPlatform;
  deviceInfo?: DeviceInfo;
}

/**
 * Hook to get push tokens for the current user
 */
export function usePushTokens() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['push-tokens'],
    queryFn: async (): Promise<PushToken[]> => {
      const {
        data: { user },
      } = await client.auth.getUser();

      if (!user) {
        return [];
      }

      const { data, error } = await client
        .from('user_push_tokens')
        .select('*')
        .eq('user_id', user.id)
        .order('last_used_at', { ascending: false, nullsFirst: false });

      if (error) {
        throw error;
      }

      return (data || []).map(mapPushToken);
    },
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to register a push token for the current user
 */
export function useRegisterPushToken() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RegisterPushTokenParams): Promise<PushToken> => {
      const {
        data: { user },
      } = await client.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Upsert the token (update if exists, insert if not)
      const { data, error } = await client
        .from('user_push_tokens')
        .upsert(
          {
            user_id: user.id,
            token: params.token,
            platform: params.platform,
            device_info: (params.deviceInfo || {}) as Json,
            is_active: true,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id,token',
          },
        )
        .select()
        .single();

      if (error) {
        throw error;
      }

      return mapPushToken(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-tokens'] });
    },
  });
}

/**
 * Hook to deactivate a push token
 */
export function useDeactivatePushToken() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokenId: string): Promise<void> => {
      const { error } = await client
        .from('user_push_tokens')
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', tokenId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-tokens'] });
    },
  });
}

/**
 * Hook to delete a push token
 */
export function useDeletePushToken() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokenId: string): Promise<void> => {
      const { error } = await client
        .from('user_push_tokens')
        .delete()
        .eq('id', tokenId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['push-tokens'] });
    },
  });
}

/**
 * Hook to manage push notification permissions
 */
export function usePushPermission() {
  const registerToken = useRegisterPushToken();

  const requestPermission = useCallback(async (): Promise<boolean> => {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('Push notifications not supported in this browser');
      return false;
    }

    // Check current permission state
    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('Push notifications have been denied');
      return false;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }, []);

  const registerWebPush = useCallback(
    async (swRegistration: ServiceWorkerRegistration): Promise<boolean> => {
      try {
        // Get VAPID public key from environment
        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
        if (!vapidPublicKey) {
          console.warn('VAPID public key not configured');
          return false;
        }

        // Subscribe to push notifications
        const subscription = await swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            .buffer as ArrayBuffer,
        });

        // Register the token with our backend
        await registerToken.mutateAsync({
          token: JSON.stringify(subscription.toJSON()),
          platform: 'web',
          deviceInfo: {
            deviceName: navigator.userAgent.substring(0, 100),
          },
        });

        return true;
      } catch (error) {
        console.error('Failed to register web push:', error);
        return false;
      }
    },
    [registerToken],
  );

  return {
    requestPermission,
    registerWebPush,
    isRegistering: registerToken.isPending,
  };
}

/**
 * Convert VAPID key to Uint8Array for push subscription
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Hook for Expo push notification registration (React Native)
 */
export function useExpoPushRegistration() {
  const registerToken = useRegisterPushToken();

  const registerExpoPush = useCallback(
    async (
      expoPushToken: string,
      platform: 'ios' | 'android',
    ): Promise<boolean> => {
      try {
        await registerToken.mutateAsync({
          token: expoPushToken,
          platform,
        });

        return true;
      } catch (error) {
        console.error('Failed to register Expo push token:', error);
        return false;
      }
    },
    [registerToken],
  );

  return {
    registerExpoPush,
    isRegistering: registerToken.isPending,
  };
}
